/* eslint-disable max-lines */
import { ArrowPathIcon, XCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { cancelPackage } from "../../services/Esim.service";
import { resolveError } from "../../utils/apiErrorMessage";
import InfoTip from "../reservation_v2/cancellation/InfoTip";
import Button from "../ui/Button";
import IconBtn from "../ui/IconBtn";
import Modal from "../ui/Modal";

interface ICancelEsimModal {
  uniqueReservationGuid: string;
  defaultRefundAmount: number;
  onSuccess?: () => void;
}

// A package paid entirely with Gjirafa Money has no cash, and a prefilled "0" would reach the backend as
// "cancel without refund", which also keeps the spent credits. Empty asks for the full refund instead.
export const initialRefundAmount = (cashPaid: number): string =>
  cashPaid > 0 ? String(cashPaid) : "";

const CancelEsimModal: React.FC<ICancelEsimModal> = ({
  uniqueReservationGuid,
  defaultRefundAmount,
  onSuccess
}) => {
  const [show, setShow] = useState(false);
  const [refundAmount, setRefundAmount] = useState<string>(
    initialRefundAmount(defaultRefundAmount)
  );
  const [refundInCredits, setRefundInCredits] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleOpen = () => {
    setRefundAmount(initialRefundAmount(defaultRefundAmount));
    setRefundInCredits(false);
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
    setRefundAmount(initialRefundAmount(defaultRefundAmount));
    setRefundInCredits(false);
  };

  const handleCancel = async () => {
    // An empty field means "refund whatever this reservation owes" — cancelPackage turns that into an
    // explicit refundFullCashAmount=true, because the backend refuses a request that names no amount at
    // all. It must not collapse to 0, which the backend reads as "cancel without refunding".
    const trimmed = refundAmount.trim();
    const parsedAmount = trimmed === "" ? undefined : Number(trimmed);

    if (
      parsedAmount !== undefined &&
      (Number.isNaN(parsedAmount) || parsedAmount < 0)
    ) {
      toast.error("Shuma për rimbursim nuk është valide");
      return;
    }

    setIsLoading(true);
    try {
      await cancelPackage(uniqueReservationGuid, parsedAmount, refundInCredits);
      toast.success("Paketa është anuluar me sukses");
      handleClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      toast.error(
        resolveError(error, "Ndodhi një gabim gjatë anulimit të paketës")
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <IconBtn className="w-8 h-8" onClick={handleOpen}>
        <XCircleIcon className="w-8 h-8 text-red-500 hover:text-red-100" />
      </IconBtn>
      <Modal
        position="center"
        show={show}
        onClose={handleClose}
        hasHeader={false}
        calcHeight={false}
        modalWidth="380px"
        modalHeight="310px"
        buttonElement={
          <Button onClick={handleCancel} disabled={isLoading} className="flex items-center">
            {isLoading && <ArrowPathIcon className="w-4 h-4 animate-spin mr-2" />}
            Konfirmo
          </Button>
        }
      >
        <div className="flex justify-center mt-4 relative">
          <h3 className="text-lg font-semibold">Konfirmo anulimin e paketës</h3>
          <IconBtn
            className="absolute right-2 -top-2"
            onClick={handleClose}
          >
            <XMarkIcon className="w-4 h-4 text-neutral" strokeWidth={2} />
          </IconBtn>
        </div>
        <p className="justify-center w-full flex mt-2 text-gray-600 text-sm">
          Ky veprim do të anulojë paketën e rezervuar.
        </p>
        <div className="px-6 mt-3">
          {/* The prefill is the cash captured for this package, net of credits and promo codes, not the
              list price. */}
          <div className="flex items-center gap-1 mb-1">
            <label htmlFor="refundAmount" className="text-sm font-medium text-gray-700">
              Shuma për rimbursim (€)
            </label>
            <InfoTip
              text={
                "Sa para pagoi vërtet klienti. Bosh: rimbursim i plotë, me gjithë Gjirafa Money. " +
                "0: pa rimbursim, as Gjirafa Money nuk kthehet."
              }
            />
          </div>
          <input
            id="refundAmount"
            type="number"
            step="0.01"
            min="0"
            value={refundAmount}
            placeholder="Rimbursim i plotë"
            onChange={(e) => setRefundAmount(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none
              focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isLoading}
          />
          <div className="flex items-center mt-3">
            <input
              id="refundInCredits"
              type="checkbox"
              checked={refundInCredits}
              onChange={(e) => setRefundInCredits(e.target.checked)}
              disabled={isLoading}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="refundInCredits" className="ml-2 text-sm font-medium text-gray-700">
              Rimburso në Gjirafa Money (vlen vetëm për eSIM)
            </label>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CancelEsimModal;
